/**
 * Secure-context-safe UUID v4 generator.
 *
 * `crypto.randomUUID()` is **only** available in secure contexts (HTTPS or
 * localhost). The project's nginx serves over plain HTTP (CLAUDE.md notes
 * "SSL 미적용"), so the browser's `crypto` object exists but `.randomUUID`
 * is undefined, causing runtime TypeErrors in the hook.
 *
 * `crypto.getRandomValues()` has no secure-context requirement and works
 * everywhere, so we derive UUID v4 bytes manually when `randomUUID` is
 * missing. Final fallback is `Math.random()` — worse collision resistance
 * but only hit in exotic environments where Web Crypto is entirely absent.
 *
 * Match the copyText() fallback pattern in utils/clipboard.ts.
 */
export function safeRandomUUID(): string {
  // Secure context or modern browser — use the native API when present.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // HTTP context — derive v4 UUID from crypto.getRandomValues() bytes.
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    // RFC 4122 §4.4 — set version (4) and variant (10xx) bits.
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex: string[] = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
  }

  // Last-resort fallback — weaker randomness, still RFC-shaped. Only reached
  // if both crypto.randomUUID and crypto.getRandomValues are missing.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
